import React, { useState } from "react";

import {
  useCalibrationAds,
  useCalibrationStats,
  useUpdateCalibrationAd,
  type CalibrationAd,
} from "@/hooks/useAdminBrain";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Layers,
  Trophy,
  Search,
  Filter,
  ExternalLink,
  Check,
  Loader2,
  TrendingUp,
  BarChart2,
  Star,
} from "lucide-react";
import { cn } from "@/lib/utils";

const PLATFORM_COLORS: Record<string, string> = {
  meta: "#1877F2",
  tiktok: "#010101",
  google: "#DB4437",
  youtube: "#FF0000",
};

function StatCard({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: React.ElementType; color: string }) {
  return (
    <div className="bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66]">{label}</p>
        <p className="text-xl font-bold text-[#1A1A1A]">{value}</p>
      </div>
    </div>
  );
}

function AdCard({ ad, onToggleWinner }: { ad: CalibrationAd; onToggleWinner: (id: number, isWinner: boolean) => void }) {
  const platColor = PLATFORM_COLORS[ad.platform?.toLowerCase() ?? ""] || "#6B7280";

  return (
    <div
      className={cn(
        "bg-white border rounded-xl overflow-hidden transition-all hover:border-[#E5E2DC]",
        ad.isWinner ? "border-amber-500/40" : "border-[#E5E2DC]"
      )}
      data-testid={`calibration-ad-${ad.id}`}
    >
      <div className="aspect-video bg-white overflow-hidden">
        {ad.imageUrl ? (
          <img src={ad.imageUrl} alt={ad.headline ?? "Ad creative"} className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layers className="h-8 w-8 text-slate-700" />
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide" style={{ color: platColor, backgroundColor: `${platColor}15` }}>
            {ad.platform}
          </span>
          {ad.format && <span className="text-[10px] text-[#746F66] bg-white px-1.5 py-0.5 rounded">{ad.format}</span>}
          {ad.vertical && <span className="text-[10px] text-[#746F66] bg-white px-1.5 py-0.5 rounded">{ad.vertical}</span>}
          {ad.isWinner && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 flex items-center gap-0.5">
              <Trophy className="h-2.5 w-2.5" /> Winner
            </span>
          )}
        </div>
        {ad.headline && <p className="text-xs font-medium text-[#3D3A36] truncate">{ad.headline}</p>}
        {ad.roas != null && (
          <span className="flex items-center gap-1 text-[11px] text-[#746F66]">
            <TrendingUp className="h-3 w-3 text-green-700" />
            {ad.roas.toFixed(1)}x ROAS
          </span>
        )}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => onToggleWinner(ad.id, !ad.isWinner)}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all",
              ad.isWinner ? "bg-amber-500/20 text-amber-700 hover:bg-[#3D3A36]/30" : "bg-white text-[#746F66] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
            )}
            data-testid={`toggle-winner-${ad.id}`}
          >
            {ad.isWinner ? <Check className="h-3 w-3" /> : <Trophy className="h-3 w-3" />}
            {ad.isWinner ? "Winner" : "Mark Winner"}
          </button>
          {ad.imageUrl && (
            <a href={ad.imageUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-white rounded-lg text-[#746F66] hover:text-[#1A1A1A] hover:bg-[#F5F3EE] transition-colors" data-testid={`link-view-ad-${ad.id}`}>
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const VERTICALS = ["ecommerce", "saas", "health", "finance", "beauty", "fitness", "education", "travel"];
const FORMATS = ["image", "video", "carousel", "collection"];

export default function CalibrationLibraryPage() {
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState<string | undefined>(undefined);
  const [verticalFilter, setVerticalFilter] = useState<string | undefined>(undefined);
  const [formatFilter, setFormatFilter] = useState<string | undefined>(undefined);
  const [winnerFilter, setWinnerFilter] = useState<boolean | undefined>(undefined);
  const [page, setPage] = useState(1);
  const LIMIT = 24;

  const { data: stats, isLoading: statsLoading } = useCalibrationStats();
  const { data: adsData, isLoading: adsLoading } = useCalibrationAds({
    platform: platformFilter,
    vertical: verticalFilter,
    format: formatFilter,
    isWinner: winnerFilter,
    page,
    limit: LIMIT,
  });

  const updateAd = useUpdateCalibrationAd();
  const { toast } = useToast();

  const handleToggleWinner = async (id: number, isWinner: boolean) => {
    try {
      await updateAd.mutateAsync({ id, isWinner });
      toast({ title: isWinner ? "Marked as winner" : "Winner status removed" });
    } catch {
      toast({ title: "Failed to update ad", variant: "destructive" });
    }
  };

  const filteredAds = (adsData?.ads ?? []).filter(ad => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (ad.headline?.toLowerCase().includes(q)) || (ad.body?.toLowerCase().includes(q)) || (ad.vertical?.toLowerCase().includes(q));
  });

  const PLATFORMS = ["meta", "tiktok", "google"];
  const totalPages = Math.ceil((adsData?.total ?? 0) / LIMIT);
  const hasAds = (adsData?.total ?? 0) > 0 || adsLoading;

  return (
    <div className="flex flex-col gap-5">
        <div className="flex-shrink-0 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <Layers className="h-6 w-6 text-orange-700" />
              Calibration Library
            </h1>
            <p className="text-sm text-[#746F66] mt-0.5">Reference creatives used to calibrate the Prediction Brain</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Ads" value={statsLoading ? "…" : stats?.total ?? 0} icon={Layers} color="#F97316" />
          <StatCard label="Winners" value={statsLoading ? "…" : stats?.winners ?? 0} icon={Trophy} color="#F59E0B" />
          <StatCard label="Avg ROAS" value={statsLoading ? "…" : stats?.avgRoas != null ? `${stats.avgRoas.toFixed(1)}x` : "—"} icon={TrendingUp} color="#10B981" />
          <StatCard label="Platforms" value={statsLoading ? "…" : Object.keys(stats?.byPlatform ?? {}).length} icon={BarChart2} color="#6366F1" />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#746F66]" />
              <input
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search ads..."
                className="pl-8 pr-3 py-1.5 text-sm bg-white border border-[#E5E2DC] rounded-lg text-[#1A1A1A] placeholder:text-[#746F66] focus:outline-none focus:ring-1 focus:ring-orange-500 w-48"
                data-testid="input-calibration-search"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" />
              <span className="text-[10px] uppercase tracking-wider text-[#746F66] font-semibold">Platform:</span>
              <button
                onClick={() => { setPlatformFilter(undefined); setPage(1); }}
                className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors", !platformFilter ? "bg-[#F0EDE7] text-[#1A1A1A] border-[#E5E2DC]" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                data-testid="filter-all-platforms"
              >All</button>
              {PLATFORMS.map(p => (
                <button
                  key={p}
                  onClick={() => { setPlatformFilter(platformFilter === p ? undefined : p); setPage(1); }}
                  className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors capitalize", platformFilter === p ? "bg-orange-500/20 text-orange-700 border-orange-500/40" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                  data-testid={`filter-platform-${p}`}
                >{p}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-[#746F66] font-semibold ml-5">Vertical:</span>
              <button
                onClick={() => { setVerticalFilter(undefined); setPage(1); }}
                className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors", !verticalFilter ? "bg-[#F0EDE7] text-[#1A1A1A] border-[#E5E2DC]" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                data-testid="filter-all-verticals"
              >All</button>
              {VERTICALS.map(v => (
                <button
                  key={v}
                  onClick={() => { setVerticalFilter(verticalFilter === v ? undefined : v); setPage(1); }}
                  className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors capitalize", verticalFilter === v ? "bg-indigo-500/20 text-indigo-700 border-indigo-500/40" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                  data-testid={`filter-vertical-${v}`}
                >{v}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-[#746F66] font-semibold ml-5">Format:</span>
              <button
                onClick={() => { setFormatFilter(undefined); setPage(1); }}
                className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors", !formatFilter ? "bg-[#F0EDE7] text-[#1A1A1A] border-[#E5E2DC]" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                data-testid="filter-all-formats"
              >All</button>
              {FORMATS.map(f => (
                <button
                  key={f}
                  onClick={() => { setFormatFilter(formatFilter === f ? undefined : f); setPage(1); }}
                  className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors capitalize", formatFilter === f ? "bg-teal-500/20 text-teal-700 border-teal-500/40" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                  data-testid={`filter-format-${f}`}
                >{f}</button>
              ))}
              <button
                onClick={() => { setWinnerFilter(winnerFilter === true ? undefined : true); setPage(1); }}
                className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ml-2", winnerFilter ? "bg-amber-500/20 text-amber-700 border-amber-500/40" : "border-[#E5E2DC] text-[#746F66] hover:text-[#3D3A36] hover:border-[#E5E2DC]")}
                data-testid="filter-winners-only"
              >
                <Trophy className="h-3 w-3" /> Winners only
              </button>
            </div>
          </div>
        </div>

        {adsLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-orange-700" />
          </div>
        ) : !hasAds ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="h-16 w-16 rounded-2xl bg-white border border-[#E5E2DC] flex items-center justify-center mb-5">
              <Layers className="h-8 w-8 text-[#746F66]" />
            </div>
            <p className="text-base font-medium text-[#746F66]">No calibration data yet</p>
            <p className="text-xs text-[#746F66] mt-2 max-w-sm">
              Calibration ads are reference creatives used to train the Prediction Brain. They will be populated by the AdFlex calibration pipeline.
            </p>
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Layers className="h-8 w-8 text-[#746F66] mb-3" />
            <p className="text-sm text-[#746F66]">No ads match your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredAds.map(ad => (
              <AdCard key={ad.id} ad={ad} onToggleWinner={handleToggleWinner} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#E5E2DC] text-[#746F66] hover:text-[#1A1A1A] hover:bg-[#F5F3EE] disabled:opacity-40" data-testid="button-prev-page">Previous</Button>
            <span className="text-xs text-[#746F66]">Page {page} of {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="border-[#E5E2DC] text-[#746F66] hover:text-[#1A1A1A] hover:bg-[#F5F3EE] disabled:opacity-40" data-testid="button-next-page">Next</Button>
          </div>
        )}
      </div>
  );
}
